import { ApiPaginatedResponse } from 'constants/globalTypes'
import {
  AddProcurementPurchaseRequestOfferItemRequest,
  AddProcurementPurchaseRequestOfferRequest,
  CreateWorkflowStep,
  ProcurementApprovalPolicyResponse,
  ProcurementDashboardCountsResponse,
  ProcurementPurchaseRequestDetailsResponse,
  ProcurementPurchaseRequestItemResponse,
  ProcurementPurchaseRequestListResponse,
  ProcurementPurchaseRequestQuoteResponse,
  ResolveWorkflowStepRequest,
  SelectWinningOfferRequest,
  UpdateWorkflowStep,
} from 'types/procurement'
import { ProcurementPurchaseRequestAuditLogResponse } from 'types/procurement/auditLog.model'
import {
  CreateProcurementPurchaseOrderRequest,
  ProcurementPurchaseOrderDashboardCountsResponse,
  ProcurementPurchaseOrderResponse,
  ReceiveProcurementPurchaseOrderItemRequest,
} from 'types/procurement/purchaseOrder.model'
import { baseUrl } from 'utils/axios'

export const getProcurementPurchaseRequests = async (params: any) => {
  const response = await baseUrl.get<ApiPaginatedResponse<ProcurementPurchaseRequestListResponse>>(
    '/procurement/management/purchase/request',
    { params },
  )
  return response.data
}

export const getProcurementActionablePurchaseRequests = async (params: any) => {
  const response = await baseUrl.get<ApiPaginatedResponse<ProcurementPurchaseRequestListResponse>>(
    '/procurement/management/purchase/request/actionable',
    { params },
  )
  return response.data
}

export const getProcurementPurchaseOrders = async (params: any) => {
  const response = await baseUrl.get<ApiPaginatedResponse<ProcurementPurchaseOrderResponse>>(
    '/procurement/management/purchase/order',
    { params },
  )
  return response.data
}

export const getProcurementMyPurchaseRequests = async (params: any) => {
  const response = await baseUrl.get<ApiPaginatedResponse<ProcurementPurchaseRequestListResponse>>(
    '/procurement/management/purchase/request/my',
    { params },
  )
  return response.data
}

export const getProcurementPurchaseRequest = async (id: number | string) => {
  const response = await baseUrl.get<ProcurementPurchaseRequestDetailsResponse>(
    `/procurement/management/purchase/request/${id}`,
  )
  return response.data
}

export const approveProcurementPurchaseRequest = async (
  id: number | string,
  data: ResolveWorkflowStepRequest,
) => {
  const response = await baseUrl.post<void>(
    `/procurement/management/purchase/request/${id}/workflow-step/resolve`,
    data,
  )
  return response.data
}

export const getProcurementPurchaseRequestAuditLogs = async (id: number | string) => {
  const response = await baseUrl.get<{ logs: ProcurementPurchaseRequestAuditLogResponse[] }>(
    `/procurement/management/purchase/request/${id}/audit-logs`,
  )
  return response.data.logs
}

export const getProcurementDashboardCounts = async () => {
  const response = await baseUrl.get<ProcurementDashboardCountsResponse>(
    '/procurement/management/dashboard/count',
  )
  return response.data
}

export const addProcurementPurchaseRequestOffer = async (
  id: number | string,
  data: AddProcurementPurchaseRequestOfferRequest,
) => {
  const response = await baseUrl.post<ProcurementPurchaseRequestQuoteResponse>(
    `/procurement/management/purchase/request/${id}/quote`,
    data,
  )
  return response.data
}

export const getProcurementWorkflowSteps = async (departmentId: number | string) => {
  const response = await baseUrl.get<{ policy: ProcurementApprovalPolicyResponse }>(
    `/procurement/management/approval/policy/department/${departmentId}`,
  )
  return response.data.policy
}

export const createProcurementWorkflowSteps = async (data: CreateWorkflowStep) => {
  const response = await baseUrl.post<void>('/procurement/management/approval/policy', data)
  return response.data
}

export const updateProcurementWorkflowSteps = async (
  id: number | string,
  data: UpdateWorkflowStep[],
) => {
  const response = await baseUrl.patch<void>(`/procurement/management/approval/policy/${id}`, {
    workflowSteps: data,
  })
  return response.data
}

export const uploadProcurementPurchaseRequestAttachment = async (
  id: number | string,
  quoteId: number | string,
  data: FormData,
) => {
  const response = await baseUrl.post<ProcurementPurchaseRequestQuoteResponse>(
    `/procurement/management/purchase/request/${id}/quote/${quoteId}/document`,
    data,
  )
  return response.data
}

export const selectWinningOffer = async (
  id: number | string,
  quoteId: number | string,
  data: SelectWinningOfferRequest,
) => {
  const response = await baseUrl.post<void>(
    `/procurement/management/purchase/request/${id}/quote/${quoteId}/select`,
    data,
  )
  return response.data
}

export const uploadProcurementPurchaseRequestImages = async (
  id: number | string,
  itemId: number | string,
  data: FormData,
) => {
  const response = await baseUrl.post<void>(
    `/procurement/management/purchase/request/${id}/item/${itemId}/images`,
    data,
  )
  return response.data
}

export const mapToSku = async (
  requestId: number | string,
  data: {
    procurementPurchaseRequestItemId: number
    inventoryItemId: number
  },
) => {
  const response = await baseUrl.post<ProcurementPurchaseRequestListResponse>(
    `/procurement/management/purchase/request/${requestId}/item/${data.procurementPurchaseRequestItemId}/resolve`,
    { inventoryItemId: data.inventoryItemId },
  )
  return response.data
}

export const createProcurementPurchaseOrder = async (
  data: CreateProcurementPurchaseOrderRequest,
) => {
  const response = await baseUrl.post<ProcurementPurchaseOrderResponse>(
    '/procurement/management/purchase/order',
    data,
  )
  return response.data
}

export const receiveProcurementPurchaseOrderItem = async (
  orderId: number | string,
  data: ReceiveProcurementPurchaseOrderItemRequest,
) => {
  const response = await baseUrl.post<ProcurementPurchaseOrderResponse>(
    `/procurement/management/purchase/order/${orderId}/receive`,
    data,
  )
  return response.data
}

export const getProcurementSuggestedImages = async (data: {
  inventoryItemId?: number
  name?: string
  description?: string
}) => {
  const response = await baseUrl.post<{ imageUrls: string[] }>(
    '/procurement/management/purchase/request/item/suggest-images',
    data,
  )
  return response.data.imageUrls
}

export const getProcurementPurchaseOrderDashboardCounts = async () => {
  const response = await baseUrl.get<ProcurementPurchaseOrderDashboardCountsResponse>(
    '/procurement/management/purchase/order/dashboard/count',
  )
  return response.data
}

export const markAsSentProcurementPurchaseOrder = async (orderId: number | string) => {
  const response = await baseUrl.post<void>(
    `/procurement/management/purchase/order/${orderId}/sent`,
  )
  return response.data
}
